/**
 * Visibility evaluation over the registry — the lowest logic layer of the
 * spec package (defaults, validation, and the compiler all build on it;
 * keeping it separate keeps the module graph acyclic).
 */
import {
  SECTIONS,
  DECISIONS,
  type AnyDecision,
  type Condition,
  type SectionId,
} from './registry.js';
import { DECISION_IDS, type DecisionId, type PartialGameSettings } from './types.js';

const SPINE_SECTIONS = new Set<SectionId>(SECTIONS.filter((s) => s.spine).map((s) => s.id));

/** Ids of the decisions in spine (mandatory) sections, in registry order —
 *  the wizard's review validation and requestBuild's completeness gate share
 *  this one list. */
export const SPINE_DECISION_IDS: readonly DecisionId[] = DECISION_IDS.filter((id) =>
  SPINE_SECTIONS.has(DECISIONS[id].section),
);

/** True when the decision lives in a spine (mandatory) section. */
export function isSpineDecision(id: DecisionId): boolean {
  return SPINE_SECTIONS.has(DECISIONS[id].section);
}


function conditionHolds(c: Condition, values: PartialGameSettings): boolean {
  const actual = values[c.decision as DecisionId];
  if (actual === undefined) return false;
  switch (c.op) {
    case 'eq':
      return actual === c.value;
    case 'in':
      return Array.isArray(c.value) && (c.value as readonly string[]).includes(actual as string);
    case 'includes':
      // Set membership on a `multi` decision's stored value.
      return Array.isArray(actual) && (actual as readonly string[]).includes(c.value as string);
  }
}

/** True when the decision's `visibleWhen` conditions all hold (a decision
 *  without conditions is always visible). */
export function isVisible(id: DecisionId, values: PartialGameSettings): boolean {
  const decision: AnyDecision = DECISIONS[id];
  return (decision.visibleWhen ?? []).every((c) => conditionHolds(c, values));
}
